import { type FC, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  FormBuilder,
  lockSchema,
  SECRET_PLACEHOLDER,
} from "@/shared/components/Form";
import {
  useCurrentPlatformQuery,
  useUpdateStreamingSettingsMutation,
} from "@/partner/entities/platforms";
import {
  extractMutationErrorDetail,
  resolveMutationErrorKey,
} from "@/partner/shared/lib";
import { makeStreamingSettingsSchema } from "../model/schemas";
import { buildCloudflareTokenUrl } from "../model/cloudflareTokenUrl";

const CONNECTION_STATUS_KEYS: Record<"connected" | "notConnected", string> = {
  connected: "streamingSettings.status.connected",
  notConnected: "streamingSettings.status.notConnected",
};

/**
 * Коды отказа бекенда (extensions.code) и тексты под них. Сообщение
 * сервера на экран не выводится: у Cloudflare это строка вида «Cloudflare
 * Alerting API error: 403», по которой партнёру нечего делать. Всё, чего
 * нет в мапе, показывается общим текстом со статусом в скобках.
 */
const MUTATION_ERROR_KEYS: Record<string, string> = {
  INVALID_CLOUDFLARE_TOKEN: "streamingSettings.errors.invalidToken",
  CLOUDFLARE_TOKEN_MULTIPLE_ACCOUNTS:
    "streamingSettings.errors.multipleAccounts",
  CLOUDFLARE_TOKEN_MISSING_STREAM_PERMISSION:
    "streamingSettings.errors.missingStreamPermission",
  CLOUDFLARE_TOKEN_MISSING_NOTIFICATIONS_PERMISSION:
    "streamingSettings.errors.missingNotificationsPermission",
  CLOUDFLARE_API_ERROR: "streamingSettings.errors.apiError",
};

const FALLBACK_ERROR_KEY = "streamingSettings.errors.apiError";

export const StreamingSettingsCard: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, refetch } = useCurrentPlatformQuery();
  const settings = data?.currentPlatform?.streamingSettings;
  const usesDefaultCredentials = Boolean(
    !settings?.connectedAt && data?.currentPlatform?.streamingAvailable
  );

  // Пока аккаунт подключён, поле токена заблокировано и показывает точки;
  // кнопки «Отключить» у Cloudflare нет, поэтому редактирование открывает
  // «Заменить токен».
  const [editing, setEditing] = useState(false);
  const locked = Boolean(settings?.connectedAt) && !editing;
  const schema = useMemo(() => {
    const base = makeStreamingSettingsSchema(t);
    return locked ? lockSchema(base) : base;
  }, [t, locked]);
  const platformSlug = data?.currentPlatform?.slug;
  const tokenUrl = useMemo(
    () =>
      buildCloudflareTokenUrl(
        platformSlug ? `ottkit ${platformSlug}` : "ottkit"
      ),
    [platformSlug]
  );

  const [updateStreamingSettings, { loading }] =
    useUpdateStreamingSettingsMutation({
      onCompleted: () => {
        enqueueSnackbar(t("streamingSettings.snackbar.connected"), {
          variant: "success",
        });
        setEditing(false);
        refetch();
      },
      onError: (error) => {
        enqueueSnackbar(
          t(
            resolveMutationErrorKey(
              error,
              MUTATION_ERROR_KEYS,
              FALLBACK_ERROR_KEY
            ),
            { code: extractMutationErrorDetail(error) }
          ),
          { variant: "error" }
        );
      },
    });

  return (
    <Card sx={{ mt: 2 }}>
      {loading && <LinearProgress />}
      <CardHeader
        title={t("streamingSettings.cardTitle")}
        action={
          settings?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="success"
                label={t(CONNECTION_STATUS_KEYS.connected)}
              />
              <Chip
                size="small"
                variant="outlined"
                label={settings.apiTokenMask}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${t("streamingSettings.accountLabel")}: ${settings.accountId}`}
              />
            </Stack>
          ) : (
            <Chip size="small" label={t(CONNECTION_STATUS_KEYS.notConnected)} />
          )
        }
      />
      <CardContent>
        {usesDefaultCredentials && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("streamingSettings.defaultCredentialsHint")}
          </Typography>
        )}
        {/* Форма токена Cloudflare открывается с уже выбранными правами —
            клиенту остаётся нажать Create и скопировать результат */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("streamingSettings.createTokenHint")}
          </Typography>
          <Button
            href={tokenUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("streamingSettings.createTokenButton")}
          </Button>
        </Stack>
        {loading && (
          <Alert
            severity="info"
            icon={<CircularProgress size={16} />}
            sx={{ mb: 2 }}
          >
            {t("streamingSettings.connecting")}
          </Alert>
        )}
        <FormBuilder
          key={locked ? "locked" : "editable"}
          formId="streaming-settings-form"
          schema={schema}
          initialValues={{ apiToken: locked ? SECRET_PLACEHOLDER : "" }}
          onSubmit={async (values) => {
            await updateStreamingSettings({
              variables: {
                input: { apiToken: String(values.apiToken) },
              },
            });
          }}
          submitButton={!locked}
          resetButton={false}
          submitButtonText={t("streamingSettings.submit")}
          disabled={loading || locked}
          actionsAlign="end"
        />
        {settings?.connectedAt && (
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            disabled={loading}
            onClick={() => setEditing(!editing)}
          >
            {editing
              ? t("streamingSettings.cancelReplace")
              : t("streamingSettings.replaceToken")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
